import { useState, useEffect } from 'react';
import {
  Truck,
  Plus,
  Search,
  MoreHorizontal,
  Edit2,
  Trash2,
  Loader2,
  Phone,
  Mail,
  FileText,
  Grid3X3,
  List,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { subscribeToSuppliers, unsubscribe } from '@/lib/realtime';

interface Supplier {
  id: string;
  organization_id: string;
  name: string;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  address: { line1?: string; city?: string; parish?: string } | null;
  payment_terms: string | null;
  notes: string | null;
  is_active: boolean;
  created_at: string;
}

export default function Suppliers() {
  const { organization } = useAuth();
  const queryClient = useQueryClient();

  // Real-time subscription for supplier updates
  useEffect(() => {
    if (!organization?.id) return;
    const channel = subscribeToSuppliers(organization.id, () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    });
    return () => { unsubscribe(channel); };
  }, [organization?.id, queryClient]);

  const [searchQuery, setSearchQuery] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [viewMode, setViewMode] = useState<'card' | 'list'>('card');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    contact_name: '',
    email: '',
    phone: '',
    address_line1: '',
    address_city: '',
    address_parish: '',
    payment_terms: '',
    notes: '',
  });
  const [formError, setFormError] = useState<string | null>(null);

  // Fetch suppliers
  const { data: suppliers = [], isLoading } = useQuery({
    queryKey: ['suppliers', organization?.id],
    queryFn: async () => {
      if (!organization?.id) return [];
      const { data, error } = await supabase
        .from('suppliers')
        .select('*')
        .eq('organization_id', organization.id)
        .order('name');
      if (error) throw error;
      return data as Supplier[];
    },
    enabled: !!organization?.id,
  });

  // Create supplier mutation
  const createSupplier = useMutation({
    mutationFn: async (supplierData: Partial<Supplier>) => {
      const { data, error } = await supabase
        .from('suppliers')
        .insert(supplierData)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      toast.success('Supplier created');
    },
  });

  // Update supplier mutation
  const updateSupplier = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Supplier> }) => {
      const { data, error } = await supabase
        .from('suppliers')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      toast.success('Supplier updated');
    },
  });

  // Delete supplier mutation
  const deleteSupplier = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('suppliers')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      toast.success('Supplier deleted');
    },
  });

  // Filter suppliers
  const filteredSuppliers = suppliers.filter(supplier => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        supplier.name.toLowerCase().includes(query) ||
        supplier.contact_name?.toLowerCase().includes(query) ||
        supplier.email?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const handleCreateSupplier = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!formData.name || !organization?.id) {
      setFormError('Supplier name is required');
      return;
    }

    try {
      await createSupplier.mutateAsync({
        organization_id: organization.id,
        name: formData.name,
        contact_name: formData.contact_name || null,
        email: formData.email || null,
        phone: formData.phone || null,
        address: {
          line1: formData.address_line1,
          city: formData.address_city,
          parish: formData.address_parish,
        },
        payment_terms: formData.payment_terms || null,
        notes: formData.notes || null,
        is_active: true,
      });
      setCreateDialogOpen(false);
      resetForm();
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : 'Failed to create supplier');
    }
  };

  const handleEditSupplier = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!selectedSupplier || !formData.name) {
      setFormError('Supplier name is required');
      return;
    }

    try {
      await updateSupplier.mutateAsync({
        id: selectedSupplier.id,
        updates: {
          name: formData.name,
          contact_name: formData.contact_name || null,
          email: formData.email || null,
          phone: formData.phone || null,
          address: {
            line1: formData.address_line1,
            city: formData.address_city,
            parish: formData.address_parish,
          },
          payment_terms: formData.payment_terms || null,
          notes: formData.notes || null,
        },
      });
      setEditDialogOpen(false);
      resetForm();
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : 'Failed to update supplier');
    }
  };

  const handleDeleteSupplier = async (supplier: Supplier) => {
    if (!confirm(`Are you sure you want to delete "${supplier.name}"?`)) return;
    try {
      await deleteSupplier.mutateAsync(supplier.id);
    } catch (err) {
      toast.error('Error', { description: 'Failed to delete supplier' });
    }
  };

  const handleToggleActive = async (supplier: Supplier, isActive: boolean) => {
    try {
      await updateSupplier.mutateAsync({
        id: supplier.id,
        updates: { is_active: isActive },
      });
    } catch (err) {
      toast.error('Error', { description: 'Failed to update supplier' });
    }
  };

  const openEditDialog = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    const address = supplier.address as { line1?: string; city?: string; parish?: string; state?: string } | null;
    setFormData({
      name: supplier.name,
      contact_name: supplier.contact_name || '',
      email: supplier.email || '',
      phone: supplier.phone || '',
      address_line1: address?.line1 || '',
      address_city: address?.city || '',
      address_parish: address?.parish || address?.state || '',
      payment_terms: supplier.payment_terms || '',
      notes: supplier.notes || '',
    });
    setEditDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      contact_name: '',
      email: '',
      phone: '',
      address_line1: '',
      address_city: '',
      address_parish: '',
      payment_terms: '',
      notes: '',
    });
    setFormError(null);
    setSelectedSupplier(null);
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold">Supplier Management</h2>
          <p className="text-sm text-muted-foreground">
            Manage vendors and suppliers for purchase orders
          </p>
        </div>
        <Button variant="blaze" className="gap-2" onClick={() => setCreateDialogOpen(true)}>
          <Plus className="h-4 w-4" />
          Add Supplier
        </Button>
      </div>

      {/* Search and View Toggle */}
      <div className="flex items-end gap-4 mb-6">
        <div className="flex-1 max-w-sm">
          <Label className="mb-1.5 block">Search suppliers</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Name, contact, or email"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>
        
        {/* View Toggle */}
        <div className="flex rounded-lg border overflow-hidden">
          <button
            onClick={() => setViewMode('card')}
            className={cn(
              'p-2 transition-colors',
              viewMode === 'card'
                ? 'bg-primary text-primary-foreground'
                : 'bg-background hover:bg-muted'
            )}
          >
            <Grid3X3 className="h-4 w-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={cn(
              'p-2 transition-colors',
              viewMode === 'list'
                ? 'bg-primary text-primary-foreground'
                : 'bg-background hover:bg-muted'
            )}
          >
            <List className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Suppliers Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading suppliers...</span>
          </div>
        </div>
      ) : filteredSuppliers.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <Truck className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="font-medium mb-2">No suppliers found</h3>
              <p className="text-sm text-muted-foreground mb-4">
                {searchQuery ? 'Try a different search term' : 'Add your first supplier to get started'}
              </p>
              <Button variant="blaze" className="gap-2" onClick={() => setCreateDialogOpen(true)}>
                <Plus className="h-4 w-4" />
                Add Supplier
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : viewMode === 'card' ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredSuppliers.map((supplier) => (
            <Card key={supplier.id} className={cn(!supplier.is_active && 'opacity-60')}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      'w-10 h-10 rounded-lg flex items-center justify-center',
                      supplier.is_active ? 'bg-emerald-500/10' : 'bg-muted'
                    )}>
                      <Truck className={cn(
                        'h-5 w-5',
                        supplier.is_active ? 'text-emerald-600' : 'text-muted-foreground'
                      )} />
                    </div>
                    <div>
                      <h3 className="font-semibold">{supplier.name}</h3>
                      {supplier.contact_name && (
                        <p className="text-sm text-muted-foreground">
                          {supplier.contact_name}
                        </p>
                      )}
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEditDialog(supplier)}>
                        <Edit2 className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDeleteSupplier(supplier)}
                        className="text-red-600"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Contact Info */}
                <div className="space-y-2 mb-4">
                  {supplier.phone && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Phone className="h-3.5 w-3.5" />
                      {supplier.phone}
                    </div>
                  )}
                  {supplier.email && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Mail className="h-3.5 w-3.5" />
                      {supplier.email}
                    </div>
                  )}
                  {supplier.payment_terms && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <FileText className="h-3.5 w-3.5" />
                      {supplier.payment_terms}
                    </div>
                  )}
                </div>

                {/* Status Toggle */}
                <div className="flex items-center justify-between pt-3 border-t">
                  <span className="text-sm font-medium">Active</span>
                  <Switch
                    checked={supplier.is_active}
                    onCheckedChange={(checked) => handleToggleActive(supplier, checked)}
                  />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        /* List View */
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-4 font-medium text-muted-foreground">Supplier</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Contact</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Email</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Phone</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Status</th>
                    <th className="text-right p-4 font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSuppliers.map((supplier) => (
                    <tr key={supplier.id} className={cn('border-b last:border-0 hover:bg-muted/50', !supplier.is_active && 'opacity-60')}>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            'w-8 h-8 rounded-lg flex items-center justify-center',
                            supplier.is_active ? 'bg-emerald-500/10' : 'bg-muted'
                          )}>
                            <Truck className={cn(
                              'h-4 w-4',
                              supplier.is_active ? 'text-emerald-600' : 'text-muted-foreground'
                            )} />
                          </div>
                          <span className="font-medium">{supplier.name}</span>
                        </div>
                      </td>
                      <td className="p-4 text-muted-foreground">
                        {supplier.contact_name || '—'}
                      </td>
                      <td className="p-4 text-muted-foreground">
                        {supplier.email || '—'}
                      </td>
                      <td className="p-4 text-muted-foreground">
                        {supplier.phone || '—'}
                      </td>
                      <td className="p-4">
                        <Badge
                          variant="outline"
                          className={cn(
                            'gap-1',
                            supplier.is_active
                              ? 'bg-green-50 text-green-700 border-green-200'
                              : 'bg-gray-50 text-gray-600 border-gray-200'
                          )}
                        >
                          {supplier.is_active ? (
                            <CheckCircle className="h-3 w-3" />
                          ) : (
                            <XCircle className="h-3 w-3" />
                          )}
                          {supplier.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="p-4 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(supplier)}>
                              <Edit2 className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleToggleActive(supplier, !supplier.is_active)}
                            >
                              {supplier.is_active ? (
                                <>
                                  <XCircle className="h-4 w-4 mr-2" />
                                  Deactivate
                                </>
                              ) : (
                                <>
                                  <CheckCircle className="h-4 w-4 mr-2" />
                                  Activate
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDeleteSupplier(supplier)}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Supplier Dialog */}
      <ResponsiveModal open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <ResponsiveModalContent maxWidth="md">
          <form onSubmit={handleCreateSupplier}>
            <ResponsiveModalHeader>
              <ResponsiveModalTitle>Add New Supplier</ResponsiveModalTitle>
              <ResponsiveModalDescription>
                Add a vendor for purchase orders
              </ResponsiveModalDescription>
            </ResponsiveModalHeader>

            <ResponsiveModalBody>
              <div className="grid gap-4">
                {formError && (
                  <div className="p-3 text-sm text-red-600 bg-red-50 dark:bg-red-950/20 rounded-lg">
                    {formError}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Company Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., ACME Foods"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_name">Contact Name</Label>
                    <Input
                      id="contact_name"
                      value={formData.contact_name}
                      onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                      placeholder="e.g., John Smith"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="orders@supplier.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="payment_terms">Payment Terms</Label>
                  <Input
                    id="payment_terms"
                    value={formData.payment_terms}
                    onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                    placeholder="e.g., Net 30"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes..."
                    rows={2}
                  />
                </div>
              </div>
            </ResponsiveModalBody>

            <ResponsiveModalFooter>
              <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="blaze" disabled={createSupplier.isPending}>
                {createSupplier.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Create Supplier
              </Button>
            </ResponsiveModalFooter>
          </form>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Edit Supplier Dialog */}
      <ResponsiveModal open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <ResponsiveModalContent maxWidth="md">
          <form onSubmit={handleEditSupplier}>
            <ResponsiveModalHeader>
              <ResponsiveModalTitle>Edit Supplier</ResponsiveModalTitle>
              <ResponsiveModalDescription>
                Update supplier details
              </ResponsiveModalDescription>
            </ResponsiveModalHeader>

            <ResponsiveModalBody>
              <div className="grid gap-4">
                {formError && (
                  <div className="p-3 text-sm text-red-600 bg-red-50 dark:bg-red-950/20 rounded-lg">
                    {formError}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit_name">Company Name *</Label>
                    <Input
                      id="edit_name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit_contact_name">Contact Name</Label>
                    <Input
                      id="edit_contact_name"
                      value={formData.contact_name}
                      onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit_email">Email</Label>
                    <Input
                      id="edit_email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit_phone">Phone</Label>
                    <Input
                      id="edit_phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit_payment_terms">Payment Terms</Label>
                  <Input
                    id="edit_payment_terms"
                    value={formData.payment_terms}
                    onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit_notes">Notes</Label>
                  <Textarea
                    id="edit_notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={2}
                  />
                </div>
              </div>
            </ResponsiveModalBody>

            <ResponsiveModalFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="blaze" disabled={updateSupplier.isPending}>
                {updateSupplier.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Save Changes
              </Button>
            </ResponsiveModalFooter>
          </form>
        </ResponsiveModalContent>
      </ResponsiveModal>
    </div>
  );
}

